import { BooleanInput } from '@angular/cdk/coercion';
import {
  Component,
  ViewEncapsulation,
  booleanAttribute,
  computed,
  input,
  output,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import {
  MatCalendar,
  MatDatepicker,
  MatDatepickerInput,
  MatDatepickerToggle,
} from '@angular/material/datepicker';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltip } from '@angular/material/tooltip';
import { DatePipe } from '../../pipes/date.pipe';
import { isRequired } from '../../utilities/form-control.util';
import { ShowField } from '../show-field/show-field.component';

type StartView = 'month' | 'year' | 'multi-year';

@Component({
  selector: 'procure-input-date',
  templateUrl: './input-date.component.html',
  styleUrl: './input-date.component.scss',
  imports: [
    MatFormField,
    MatIcon,
    MatTooltip,
    MatDatepicker,
    MatDatepickerToggle,
    ReactiveFormsModule,
    ShowField,
    DatePipe,
    MatLabel,
    MatDatepickerInput,
    MatInputModule,
  ],
  providers: [{ provide: MatCalendar, useValue: { firstDayOfWeek: 1 } }],
  changeDetection: ChangeDetectionStrategy.Eager,
  encapsulation: ViewEncapsulation.None,
})
export class InputDate {
  readonly undo = output<void>();
  readonly changed = output<void>();

  readonly label = input<string>('');
  readonly hint = input<string>('');
  readonly control = input.required<FormControl>();
  readonly startAt = input<Date | null>(null);
  readonly startView = input<StartView>('month');
  readonly id = input<string | null>(null);
  readonly max = input<Date | null>(null);
  readonly min = input<Date | null>(null);
  readonly placeholder = input<string>('');

  readonly prefilled = computed(() => this.highlighted() && !this.showMode());

  readonly highlighted = input<boolean, BooleanInput>(false, {
    transform: booleanAttribute,
  });

  readonly showMode = input<boolean, BooleanInput>(false, {
    transform: booleanAttribute,
  });

  readonly disabled = input<boolean, BooleanInput>(false, {
    transform: booleanAttribute,
  });

  readonly clearable = input<boolean, BooleanInput>(true, {
    transform: booleanAttribute,
  });

  protected get value(): string {
    const control = this.control();

    if (control?.value != null) {
      return control.value;
    }

    return '';
  }

  protected get isRequired(): boolean {
    return isRequired(this.control());
  }

  clear(): void {
    this.control().setValue(null);
  }

  onUndo(): void {
    this.undo.emit();
  }

  onChange(): void {
    this.changed.emit();
  }

  openDatepicker(datepicker: MatDatepicker<unknown>): void {
    const control = this.control();

    if (this.disabled() || control.disabled) {
      return;
    }

    datepicker.open();
  }
}
